import { useEffect, useState } from "react";

import { baseAPI } from "../../hooks/apiClient";
const PERMISSIONS = [
  "Orders and order status",
  "Customer names and contact details",
  "Products and inventory",
];

function cleanShop(value) {
  return value
    .trim()
    .replace(/^https?:\/\//i, "")
    .replace(/\/.*$/, "");
}

export default function ConnectShopify() {
  const [shop, setShop] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [connected, setConnected] = useState(false);

  // Shows the success banner after Shopify redirects back: /settings?connected=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("connected") === "1") setConnected(true);
    if (params.get("error"))
      setError("Shopify could not connect your store. Try again.");
  }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    setError("");

    const value = cleanShop(shop);
    if (!value) return setError("Enter your store link to continue.");

    try {
      setLoading(true);
      const res = await baseAPI.post("shopify/connect", { shop: value });

      // only ever send the browser to a Shopify address
      if (
        !/^https:\/\/[a-z0-9-]+\.myshopify\.com\//.test(res.data?.url || "")
      ) {
        setError("Something went wrong. Try again.");
        return;
      }
      window.location.href = res.data.url;
    } catch (err) {
      console.error(err);
      if (!err.response) {
        setError(
          "Can't reach the server. Check your connection and try again.",
        );
      } else {
        setError(
          err.response.data?.error || "Something went wrong. Try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-10 sm:py-16">
      <div className="mx-auto w-full max-w-2xl">
        <p className="text-sm text-slate-500">Settings / Integrations</p>
        <h1 className="mt-2 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
          Connect your Shopify store
        </h1>
        <p className="mt-2 max-w-lg text-slate-600">
          Bring your orders and customers into the CRM. You approve access on
          Shopify, and you can disconnect at any time.
        </p>

        {connected && (
          <div
            role="status"
            className="mt-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900"
          >
            <svg
              className="mt-0.5 h-5 w-5 shrink-0"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.58 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <p className="font-medium">Store connected</p>
              <p className="text-sm text-emerald-800">
                Your data will start syncing in a few minutes.
              </p>
            </div>
          </div>
        )}

        <form
          onSubmit={handleConnect}
          noValidate
          className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8"
        >
          <label
            htmlFor="shop"
            className="block text-sm font-medium text-slate-900"
          >
            Store link
          </label>

          <div
            className={`mt-2 flex items-stretch overflow-hidden rounded-lg border bg-white transition focus-within:ring-2 ${
              error
                ? "border-red-400 focus-within:ring-red-200"
                : "border-slate-300 focus-within:border-emerald-600 focus-within:ring-emerald-100"
            }`}
          >
            <input
              id="shop"
              type="text"
              inputMode="url"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              placeholder="my-store"
              value={shop}
              onChange={(e) => {
                setShop(e.target.value);
                if (error) setError("");
              }}
              aria-invalid={!!error}
              aria-describedby={error ? "shop-error" : "shop-hint"}
              className="min-w-0 flex-1 bg-transparent px-3.5 py-3 text-slate-900 placeholder:text-slate-400 focus:outline-none"
            />
            <span className="hidden items-center border-l border-slate-200 bg-slate-50 px-3.5 text-sm text-slate-500 sm:flex">
              .myshopify.com
            </span>
          </div>

          {error ? (
            <p
              id="shop-error"
              role="alert"
              className="mt-2 text-sm text-red-600"
            >
              {error}
            </p>
          ) : (
            <p id="shop-hint" className="mt-2 text-sm text-slate-500">
              Paste the full link or just the store name. You'll find it in your
              Shopify admin URL.
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-700 px-4 py-3 font-medium text-white transition hover:bg-emerald-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {loading && (
              <svg
                className="h-4 w-4 animate-spin motion-reduce:animate-none"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  className="opacity-25"
                />
                <path
                  d="M4 12a8 8 0 018-8"
                  stroke="currentColor"
                  strokeWidth="4"
                  strokeLinecap="round"
                  className="opacity-90"
                />
              </svg>
            )}
            {loading ? "Opening Shopify..." : "Connect store"}
          </button>

          <div className="mt-8 border-t border-slate-100 pt-6">
            <h2 className="text-sm font-medium text-slate-900">
              Shopify will ask you to allow access to
            </h2>
            <ul className="mt-3 space-y-2">
              {PERMISSIONS.map((item) => (
                <li
                  key={item}
                  className="flex items-center gap-2.5 text-sm text-slate-600"
                >
                  <svg
                    className="h-4 w-4 shrink-0 text-emerald-600"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 111.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </form>

        <p className="mt-4 text-sm text-slate-500">
          Each workspace can connect one store.
        </p>
      </div>
    </main>
  );
}
